import { textColorFields } from '../_shared/textColor.js';
import { lineHeightField } from '../../blocks/_shared/blockFields.js';
/**
 * sticky-cta — a 1:1 rebuild of Shrine Pro's "Sticky CTA" section (shrine-pro-ref/sections/sticky-cta.liquid).
 * A `position:fixed` bar pinned to the bottom of the viewport that slides into view once the reader has
 * scrolled past a threshold (always / after X px / after X% of the page). It carries a title, an optional
 * subtitle, an optional image, an optional 5-star rating row, and a single CTA. The section holds NO blocks;
 * it is a floating call-to-action, not a content container.
 *
 * The CTA has an ACTION (`buttonAction`), which Shrine does not: by default it takes the shopper to the buy
 * box on the same page — the COD order form, or the buy button — and only on the explicit "Open a link"
 * choice does it become Shrine's link button (with Shrine's disabled placeholder when the link is empty).
 * Shrine only ever knew the link, so every bar nobody had linked by hand was a dead button.
 *
 * Config is faithful to Shrine, one-for-one: the display trigger (`display` + `displayPixels` /
 * `displayPercent`, each revealed by `visibleWhen`), the content (title / subtitle / image), the CTA (label +
 * link + a custom button colour), and the per-device layout toggles (show image / title / subtitle /
 * rating, full-width button, transparent-background style) for Desktop and Mobile — where, unlike Shrine,
 * the rating stars' colour and text are per device too (see the Desktop layout fields). Field-type mapping (Shopify → ours): select→select, text→text, inline_richtext→richtext
 * (subtitle) / text (the tiny stars label), image_picker→image, url→link (its built-in `newTab` replaces
 * Shrine's separate `open_in_new_tab`), color→color, range→range, checkbox→checkbox, header→group. Ids are
 * clean camelCase; labels / options / defaults stay faithful to Shrine.
 */
/** Convenience for the literal hex defaults Shrine ships. */
const hex = (value) => ({ ref: 'custom', value });
const GENERAL = 'General';
const BUTTON = 'Button';
const DESKTOP = 'Desktop layout';
const MOBILE = 'Mobile layout';
/**
 * Shrine's 5 colour-scheme choices for the bar, PLUS our own "Custom".
 *
 * Shrine's sticky-cta has no custom entry — the bar can only wear one of the theme's schemes. That is a
 * closed loop for a seller whose brand colour is not one of the five (founder, 2026-08-27: "we should be
 * able to change its colour to a custom colour, currently we can't"), so we diverge here exactly the way
 * the sibling ports already do (vertical-ticker, testimonials, video…): a sixth `custom` value that reveals
 * a background + text colour pair. Every other value stays byte-faithful to Shrine.
 */
const SCHEME_6 = [
    { value: 'accent-1', label: 'Accent 1' },
    { value: 'accent-2', label: 'Accent 2' },
    { value: 'background-1', label: 'Background 1' },
    { value: 'background-2', label: 'Background 2' },
    { value: 'inverse', label: 'Inverse' },
    { value: 'custom', label: 'Custom' },
];
export const stickyCtaSchema = {
    type: 'sticky-cta',
    name: 'Sticky CTA',
    // v2 — the button's custom colour moved from a checkbox (`enableCustomBtnColor`) to the same one-list
    // shape the bar uses (`buttonColor`). v3 — the rating stars' colour and text stopped being shared: the
    // desktop keeps `starColor` / `starsLabel` (now in its own group), the phone gets `mobileStarColor` /
    // `mobileStarsLabel` of its own. `migrate` below carries every v1 / v2 page over untouched.
    version: 3,
    templates: ['index', 'product', 'collection', 'page'],
    fields: [
        // ── General ────────────────────────────────────────────────────────────────
        {
            id: 'display',
            type: 'select',
            label: 'Display',
            group: GENERAL,
            default: 'after_percent',
            options: [
                { value: 'always', label: 'Always' },
                { value: 'after_pixels', label: 'After scrolling X pixels' },
                { value: 'after_percent', label: 'After scrolling X% of the page' },
            ],
        },
        {
            id: 'displayPixels',
            type: 'range',
            label: 'Display after X pixels',
            group: GENERAL,
            min: 50,
            max: 2000,
            step: 50,
            unit: 'px',
            default: 500,
            visibleWhen: { field: 'display', equals: 'after_pixels' },
        },
        {
            id: 'displayPercent',
            type: 'range',
            label: 'Display after X% of the page',
            group: GENERAL,
            min: 5,
            max: 95,
            step: 5,
            unit: '%',
            default: 50,
            visibleWhen: { field: 'display', equals: 'after_percent' },
        },
        {
            id: 'title',
            type: 'text',
            label: 'Title',
            group: GENERAL,
            default: 'Sticky CTA title',
            defaultKey: 'sticky.default_title',
            // Placeholder de CONTENU, pas libellé d'interface — l'IA doit l'écrire (0059).
            aiWritable: true,
        },
        {
            id: 'subtitle',
            type: 'richtext',
            label: 'Subtitle',
            group: GENERAL,
            info: 'Optional secondary line displayed under the title.',
        },
        // « Interligne » (founder, 21/09/2026: a two-line title in the bar sat 34px apart at 19px — the page's
        // body ratio, 1.8, inherited by a bar that never set its own). One ratio for the title AND the
        // subtitle; unset = the theme's heading ratio (see Component.tsx), and the handle starts on that same
        // number so nothing jumps under her hand when she takes it over.
        { ...lineHeightField(1.3), group: GENERAL },
        { id: 'image', type: 'image', label: 'Image', group: GENERAL },
        {
            id: 'colorScheme',
            type: 'select',
            label: 'Color scheme',
            group: GENERAL,
            default: 'background-1',
            options: SCHEME_6,
        },
        {
            id: 'customBgColor',
            type: 'color',
            label: 'Custom bar background',
            group: GENERAL,
            default: hex('#111111'),
            visibleWhen: { field: 'colorScheme', equals: 'custom' },
        },
        {
            id: 'customTextColor',
            type: 'color',
            label: 'Custom bar text color',
            group: GENERAL,
            default: hex('#ffffff'),
            visibleWhen: { field: 'colorScheme', equals: 'custom' },
        },
        // ── Button ─────────────────────────────────────────────────────────────────
        {
            id: 'buttonAction',
            type: 'select',
            label: 'Button action',
            group: BUTTON,
            default: 'buy_box',
            options: [
                { value: 'buy_box', label: 'Go to the order form / add to cart' },
                { value: 'link', label: 'Open a link' },
            ],
            info: 'The order form (or the buy button) is on the same page — the bar scrolls the shopper to it.',
        },
        {
            id: 'buttonLabel',
            type: 'text',
            label: 'Button label',
            group: BUTTON,
            default: 'Learn more',
            defaultKey: 'common.learn_more',
        },
        {
            id: 'buttonLink',
            type: 'link',
            label: 'Button link',
            group: BUTTON,
            visibleWhen: { field: 'buttonAction', equals: 'link' },
        },
        // The button's colour reads like the bar's: ONE list, "Personnalisé" reveals the two colours. Shrine
        // gated it behind an "Enable custom button color" checkbox, which is a second, different shape for the
        // same decision two fields apart (founder, 2026-08-27: "la couleur du bouton doit être avec les autres,
        // simple"). `enableCustomBtnColor` is gone from the panel but still HONOURED at render for the pages
        // that stored it, so nobody's button changes colour behind their back (see Component.tsx).
        {
            id: 'buttonColor',
            type: 'select',
            label: 'Button color',
            group: BUTTON,
            default: 'theme',
            options: [
                { value: 'theme', label: 'Theme color' },
                { value: 'custom', label: 'Custom' },
            ],
        },
        {
            id: 'customBtnColor',
            type: 'color',
            label: 'Custom button background',
            group: BUTTON,
            default: hex('#dd1d1d'),
            visibleWhen: { field: 'buttonColor', equals: 'custom' },
        },
        {
            id: 'customBtnTextColor',
            type: 'color',
            label: 'Custom button text color',
            group: BUTTON,
            default: hex('#ffffff'),
            visibleWhen: { field: 'buttonColor', equals: 'custom' },
        },
        // The button's text size and weight (founder, 2026-09-21, after the same two controls landed on the
        // buy box: "le bouton cta qui n'est pas dans le [formulaire] n'est pas devenu bold"). This bar is its
        // own section and cannot read the buy box's tokens, so it carries its own pair. Unset = what the bar
        // has always painted: bold (700), and a size that follows the width (15 / 17 / 19 px) — so neither
        // control moves a published page. The range keeps NO stored default (`inheritLabel`).
        {
            id: 'buttonFontSize',
            type: 'range',
            label: 'Button text size',
            group: BUTTON,
            info: 'On « Automatic », the button shrinks its text rather than break it in two. Set a size and yours is painted, exactly as typed.',
            inheritLabel: 'Automatic (15–19 px by width)',
            default: 17,
            min: 12,
            max: 24,
            step: 1,
            unit: 'px',
        },
        {
            id: 'buttonWeight',
            type: 'select',
            label: 'Button text weight',
            group: BUTTON,
            default: '',
            options: [
                { value: '', label: '— Default (bold)' },
                { value: '400', label: 'Normal' },
                { value: '500', label: 'Medium' },
                { value: '600', label: 'Semibold' },
                { value: '700', label: 'Bold' },
                { value: '800', label: 'Extra-bold' },
            ],
        },
        // ── Desktop layout ───────────────────────────────────────────────────────────
        {
            id: 'desktopShowImage',
            type: 'checkbox',
            label: 'Show image',
            group: DESKTOP,
            default: false,
        },
        {
            id: 'desktopShowTitle',
            type: 'checkbox',
            label: 'Show title',
            group: DESKTOP,
            default: true,
        },
        {
            id: 'desktopShowSubtitle',
            type: 'checkbox',
            label: 'Show subtitle',
            group: DESKTOP,
            default: true,
        },
        {
            id: 'desktopRatingStars',
            type: 'checkbox',
            label: 'Show rating stars',
            group: DESKTOP,
            default: false,
        },
        // ── The stars' colour and text belong to ONE device each (founder, 21/09/2026: "the rating stars
        //    color must be independent, the desktop on its own and the mobile on its own"). Until v2 they sat
        //    in General, shared by both layouts; now the desktop pair lives here and the phone pair in Mobile
        //    layout, each shown only while its own "Show rating stars" is on. ──
        {
            id: 'starColor',
            type: 'color',
            label: 'Rating stars color',
            group: DESKTOP,
            default: hex('#ffcc00'),
            visibleWhen: { field: 'desktopRatingStars', equals: true },
        },
        {
            id: 'starsLabel',
            type: 'text',
            label: 'Rating stars text',
            group: DESKTOP,
            default: '(xxxx Reviews)',
            defaultKey: 'sticky.reviews_placeholder',
            visibleWhen: { field: 'desktopRatingStars', equals: true },
        },
        {
            id: 'desktopFullButtonWidth',
            type: 'checkbox',
            label: 'Button full width',
            group: DESKTOP,
            default: false,
        },
        // ── THE BAR'S THREE WIDTHS, ONE PER DEVICE (founder, 22/09/2026: "on doit pouvoir contrôler la taille
        //    du bouton, height et width […] pour mobile et desktop séparé aussi le text […] pourquoi il retourne
        //    à la ligne aussi son width dans le desktop et mobile doivent être séparés").
        //
        //    Until now the bar shared out its width by itself: the button took what its label needed
        //    (`flex-shrink:0`) and the text column got the rest — which is why a two-word title reads "MOVE /
        //    View" on a narrow bar. These three give the seller the arbitration, per device, and each one keeps
        //    NO stored value (`inheritLabel`): untouched, the bar lays out exactly as it always has.
        //
        //    The height is in PIXELS, not in % as the request put it: the bar has no height of its own to take a
        //    share of — it is as tall as what it holds — so a percentage would have nothing to measure against.
        //    The two widths ARE percentages, of the bar, like the icon-with-text block's.
        {
            id: 'desktopButtonWidth',
            type: 'range',
            label: 'Button width',
            group: DESKTOP,
            info: 'Share of the bar the button takes. Unset, it takes exactly what its text needs.',
            inheritLabel: 'Automatic (fits the text)',
            min: 10,
            max: 100,
            step: 5,
            default: 40,
            unit: '%',
        },
        {
            id: 'desktopButtonHeight',
            type: 'range',
            label: 'Button height',
            group: DESKTOP,
            info: 'Unset, the button is as tall as its text needs.',
            inheritLabel: 'Automatic (follows the text size)',
            min: 28,
            max: 96,
            step: 2,
            default: 52,
            unit: 'px',
        },
        {
            id: 'desktopTextWidth',
            type: 'range',
            label: 'Text width',
            group: DESKTOP,
            info: 'Share of the bar the title and subtitle take. Widen it when the title breaks onto two lines.',
            inheritLabel: 'Automatic (what the button leaves)',
            min: 20,
            max: 100,
            step: 5,
            default: 55,
            unit: '%',
        },
        {
            id: 'desktopTransparentBg',
            type: 'checkbox',
            label: 'Use transparent background style.',
            group: DESKTOP,
            default: false,
            info: 'This option automatically makes the button full width and hides all other elements.',
        },
        // ── Mobile layout ────────────────────────────────────────────────────────────
        { id: 'mobileShowImage', type: 'checkbox', label: 'Show image', group: MOBILE, default: false },
        { id: 'mobileShowTitle', type: 'checkbox', label: 'Show title', group: MOBILE, default: true },
        // ── The phone gets its OWN words (founder, 21/09/2026: "in the desktop I want to write a longer text
        //    but in mobile I should keep it short"). Each one is optional: left empty, the phone shows the
        //    desktop text, byte-for-byte the bar every store already has. Filled, the bar paints TWO copies
        //    and the device classes (`mobile-hidden` / `desktop-hidden`) pick one — no JS, no layout shift.
        //    The reviews text under the stars (`mobileStarsLabel`, below) is NOT a twin: see there. ──
        {
            id: 'mobileTitle',
            type: 'text',
            label: 'Mobile title',
            group: MOBILE,
            info: 'A shorter title for the phone. Left empty, the phone shows the title above.',
            visibleWhen: { field: 'mobileShowTitle', equals: true },
        },
        {
            id: 'mobileShowSubtitle',
            type: 'checkbox',
            label: 'Show subtitle',
            group: MOBILE,
            default: true,
        },
        {
            id: 'mobileSubtitle',
            type: 'richtext',
            label: 'Mobile subtitle',
            group: MOBILE,
            info: 'A shorter subtitle for the phone. Left empty, the phone shows the subtitle above.',
            visibleWhen: { field: 'mobileShowSubtitle', equals: true },
        },
        {
            id: 'mobileButtonLabel',
            type: 'text',
            label: 'Mobile button label',
            group: MOBILE,
            info: 'A shorter button label for the phone. Left empty, the phone shows the button label above.',
        },
        {
            id: 'mobileRatingStars',
            type: 'checkbox',
            label: 'Show rating stars',
            group: MOBILE,
            default: false,
        },
        {
            id: 'mobileStarColor',
            type: 'color',
            label: 'Mobile rating stars color',
            group: MOBILE,
            default: hex('#ffcc00'),
            visibleWhen: { field: 'mobileRatingStars', equals: true },
        },
        // Unlike the title / subtitle / button twins above, this one is NOT a fallback: the phone has its own
        // text, with its own default, and an empty field means stars with no text on the phone.
        {
            id: 'mobileStarsLabel',
            type: 'text',
            label: 'Mobile rating stars text',
            group: MOBILE,
            default: '(xxxx Reviews)',
            defaultKey: 'sticky.reviews_placeholder',
            visibleWhen: { field: 'mobileRatingStars', equals: true },
        },
        {
            id: 'mobileFullButtonWidth',
            type: 'checkbox',
            label: 'Button full width',
            group: MOBILE,
            default: false,
            info: 'Recommended if title and rating stars are disabled.',
        },
        // The phone's own three widths — same fields, same "unset = as before", read by the phone tier only.
        // (See the Desktop trio above for why the height is in px and the widths in %.)
        {
            id: 'mobileButtonWidth',
            type: 'range',
            label: 'Button width',
            group: MOBILE,
            info: 'Share of the bar the button takes. Unset, it takes exactly what its text needs.',
            inheritLabel: 'Automatic (fits the text)',
            min: 10,
            max: 100,
            step: 5,
            default: 50,
            unit: '%',
        },
        {
            id: 'mobileButtonHeight',
            type: 'range',
            label: 'Button height',
            group: MOBILE,
            info: 'Unset, the button is as tall as its text needs.',
            inheritLabel: 'Automatic (follows the text size)',
            min: 28,
            max: 96,
            step: 2,
            default: 48,
            unit: 'px',
        },
        {
            id: 'mobileTextWidth',
            type: 'range',
            label: 'Text width',
            group: MOBILE,
            info: 'Share of the bar the title and subtitle take. Widen it when the title breaks onto two lines.',
            inheritLabel: 'Automatic (what the button leaves)',
            min: 20,
            max: 100,
            step: 5,
            default: 45,
            unit: '%',
        },
        {
            id: 'mobileTransparentBg',
            type: 'checkbox',
            label: 'Use transparent background style.',
            group: MOBILE,
            default: false,
            info: 'This option automatically makes the button full width and hides all other elements.',
        },
        // Text colors — the bar prints a product title and a price; both follow the seller here.
        ...textColorFields(),
    ],
    maxBlocks: 0,
    /**
     * v1 → v2: the button's custom colour was gated by an `enableCustomBtnColor` checkbox; it is now the
     * `buttonColor` list ("Theme color" / "Custom"), like the bar's palette. Without this, `applyDefaults`
     * would write `buttonColor: 'theme'` onto every stored page and quietly repaint every button a seller
     * had coloured by hand. The colours themselves (`customBtnColor` / `customBtnTextColor`) keep their ids
     * and are carried through as they are.
     */
    migrate(fromVersion, settings) {
        let out = settings;
        if (fromVersion < 2 && out.buttonColor === undefined) {
            out = { ...out, buttonColor: out.enableCustomBtnColor === true ? 'custom' : 'theme' };
        }
        // v2 → v3: the phone used to borrow the desktop's star colour, and its text when it had none. Copy
        // both over so a stored bar paints on the phone exactly what it painted yesterday; a bar that never
        // set them keeps nothing and takes the fields' defaults, which are the same on both devices.
        // `undefined` ONLY, never `''`: the instance keeps the version that wrote it, so this runs again on
        // every render of the open editor — an empty phone text the seller has JUST cleared must stay empty,
        // not spring back to the desktop's.
        if (fromVersion < 3) {
            const copied = { ...out };
            if (copied.mobileStarColor === undefined && out.starColor !== undefined) {
                copied.mobileStarColor = out.starColor;
            }
            if (copied.mobileStarsLabel === undefined && out.starsLabel !== undefined) {
                copied.mobileStarsLabel = out.starsLabel;
            }
            out = copied;
        }
        return out;
    },
};
export const stickyCtaPresets = [
    {
        name: 'Sticky CTA',
        settings: {
            display: 'after_percent',
            displayPercent: 50,
            // `title` and `buttonLabel` were repeated here, identical to the fields' own defaults.
            colorScheme: 'background-1',
            desktopShowTitle: true,
            desktopShowSubtitle: true,
            mobileShowTitle: true,
            mobileShowSubtitle: true,
        },
    },
];
/**
 * How this section presents itself in "Add section": label, group, icon.
 *
 * It lives beside the schema, NOT in `index.ts`, and that is the point. `index.ts` is the module that pulls
 * in `Component.tsx`, and the builder reads `meta` for all sixty-two sections to paint the picker while
 * rendering only the handful a page actually uses. Keeping `meta` here lets `registry.lazy.gen.ts` import it
 * without dragging every section's render code into the editor's entry chunk.
 */
export const stickyCtaMeta = {
    label: 'Sticky CTA',
    group: 'Content',
    icon: 'megaphone',
};
//# sourceMappingURL=schema.js.map